import { useState, useCallback, useRef } from 'react'
import { PatientDocument } from 'common/enum/patient-document'
import { UploadService } from 'service/usecases/upload-service'
import { getUploadLogger } from 'common/utils/upload-logger'
import {
  UploadState,
  UploadResult,
  UserContext
} from 'domain/entities/upload-document'

const logger = getUploadLogger()

interface UseDocumentUploadReturn {
  uploadState: UploadState
  uploadDocument: (
    file: File,
    surgicalOrderId: number,
    documentType: PatientDocument,
    user: UserContext
  ) => Promise<string | null>
  uploadMultipleDocuments: (
    files: File[],
    surgicalOrderId: number,
    documentType: PatientDocument,
    user: UserContext
  ) => Promise<UploadResult[]>
  cancelUpload: () => void
  resetState: () => void
}

export const useDocumentUpload = (
  uploadService: UploadService
): UseDocumentUploadReturn => {
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0
  })

  const abortControllerRef = useRef<AbortController | null>(null)
  const isCancelledRef = useRef(false)
  const resetState = useCallback(() => {
    setUploadState({
      status: 'idle',
      progress: 0
    })
    isCancelledRef.current = false
  }, [])

  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    isCancelledRef.current = true
    setUploadState({
      status: 'error',
      progress: 0,
      error: new Error('Upload cancelled by user')
    })
  }, [])

  const uploadDocument = useCallback(
    async (
      file: File,
      surgicalOrderId: number,
      documentType: PatientDocument,
      user: UserContext
    ): Promise<string | null> => {
      isCancelledRef.current = false
      abortControllerRef.current = new AbortController()

      try {
        setUploadState({
          status: 'requesting_url',
          progress: 10
        })
        if (isCancelledRef.current) {
          return null
        }
        setUploadState({
          status: 'uploading',
          progress: 40
        })
        if (isCancelledRef.current) {
          return null
        }
        setUploadState({
          status: 'notifying',
          progress: 80
        })
        if (isCancelledRef.current) {
          return null
        }
        const documentId = await uploadService.uploadDocument(
          file,
          surgicalOrderId,
          documentType,
          user
        )

        if (isCancelledRef.current) {
          return null
        }

        setUploadState({
          status: 'success',
          progress: 100,
          document_id: documentId
        })

        return documentId
      } catch (error) {
        if (isCancelledRef.current) {
          return null
        }

        const uploadError =
          error instanceof Error ? error : new Error(String(error))

        setUploadState({
          status: 'error',
          progress: 0,
          error: uploadError
        })

        logger.error('[useDocumentUpload] Upload failed', uploadError)
        return null
      } finally {
        abortControllerRef.current = null
      }
    },
    [uploadService]
  )

  const uploadMultipleDocuments = useCallback(
    async (
      files: File[],
      surgicalOrderId: number,
      documentType: PatientDocument,
      user: UserContext
    ): Promise<UploadResult[]> => {
      isCancelledRef.current = false
      abortControllerRef.current = new AbortController()

      try {
        setUploadState({
          status: 'requesting_url',
          progress: 0
        })

        if (isCancelledRef.current) {
          return files.map((file) => ({
            file,
            success: false,
            error: new Error('Upload cancelled by user')
          }))
        }

        const onProgress = (completed: number, total: number) => {
          if (isCancelledRef.current) {
            return
          }

          const progressPercentage = Math.round((completed / total) * 100)

          let status: UploadState['status'] = 'uploading'
          if (progressPercentage < 30) {
            status = 'requesting_url'
          } else if (progressPercentage < 70) {
            status = 'uploading'
          } else if (progressPercentage < 100) {
            status = 'notifying'
          }

          setUploadState({
            status,
            progress: progressPercentage
          })
        }

        const results = await uploadService.uploadMultipleDocuments(
          files,
          surgicalOrderId,
          documentType,
          user,
          onProgress
        )

        if (isCancelledRef.current) {
          return files.map((file) => ({
            file,
            success: false,
            error: new Error('Upload cancelled by user')
          }))
        }

        const allSuccessful = results.every((result) => result.success)
        const anySuccessful = results.some((result) => result.success)

        if (allSuccessful) {
          setUploadState({
            status: 'success',
            progress: 100
          })
        } else if (anySuccessful) {
          setUploadState({
            status: 'success',
            progress: 100,
            error: new Error('Some files failed to upload')
          })
        } else {
          setUploadState({
            status: 'error',
            progress: 0,
            error: new Error('All files failed to upload')
          })
        }

        return results
      } catch (error) {
        if (isCancelledRef.current) {
          return files.map((file) => ({
            file,
            success: false,
            error: new Error('Upload cancelled by user')
          }))
        }

        const uploadError =
          error instanceof Error ? error : new Error(String(error))

        setUploadState({
          status: 'error',
          progress: 0,
          error: uploadError
        })

        logger.error('[useDocumentUpload] Multiple upload failed', uploadError)

        return files.map((file) => ({
          file,
          success: false,
          error: uploadError
        }))
      } finally {
        abortControllerRef.current = null
      }
    },
    [uploadService]
  )

  return {
    uploadState,
    uploadDocument,
    uploadMultipleDocuments,
    cancelUpload,
    resetState
  }
}
